import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import clsx from 'clsx'
import styled from 'styled-components'
import { fetchCruisePromoBanners } from 'actions/CruiseActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import CruisePromoTileItem from './CruisePromoTileItem'

const CruisePromoTilesWrapper = styled.div`
  &.fixed-banner {
    display: grid;
    grid-template-columns: 1fr 2fr;
  }
`

const PromoTilesCarousel = styled(CardCarousel)`
  &.centralize-items {
    .paged {
      justify-content: center;
      grid-template-columns: unset;
    }
  }

  .paged {
    align-items: center;
  }
`

interface Props {
  cruisePromoBanners: Array<App.CruisePromoBanner>
  cruiseLineId?: string
  pageSize?: number
}

function CruisePromoTiles({ cruisePromoBanners, cruiseLineId, pageSize } : Props) {
  const dispatch = useAppDispatch()

  // fetch cruise banners
  useEffect(() => {
    dispatch(fetchCruisePromoBanners())
  }, [dispatch])

  // filter out expired banners
  const banners = useMemo(() => {
    const today = new Date()

    return cruisePromoBanners.filter(banner => {
      const cruiseLinePageValidation = cruiseLineId ? banner.isVisibleOnVendorPage && banner.vendorId === cruiseLineId : !banner.isVisibleOnVendorPage
      const startDate = new Date(banner.startDate)
      const endDate = new Date(banner.endDate)
      return (
        startDate <= today &&
        endDate >= today &&
        cruiseLinePageValidation
      )
    })
  }, [cruisePromoBanners, cruiseLineId])

  const fixedBanner = useMemo(() => banners.find((banner) => banner.isFixed), [banners])
  const showFixedBanner = fixedBanner && banners.length > 3
  const carouselBanners = useMemo(() => banners.filter((banner) => {
    if (showFixedBanner && banner.isFixed) {
      return false
    }

    return true
  }), [banners, showFixedBanner])

  if (!banners.length) return null

  return <CruisePromoTilesWrapper
    className={clsx({
      'fixed-banner': showFixedBanner,
    })}
  >
    {showFixedBanner && <CruisePromoTileItem
      promoBanner={fixedBanner}
    />}

    <PromoTilesCarousel
      pageSize={pageSize ?? (showFixedBanner ? 2 : 3)}
      gap={20}
      width="auto"
      className={clsx({ 'centralize-items': carouselBanners.length < 3 && !showFixedBanner })}
    >
      {carouselBanners.map((banner) => <CruisePromoTileItem
        key={banner.id}
        promoBanner={banner}
      />)}
    </PromoTilesCarousel>
  </CruisePromoTilesWrapper>
}

const mapStateToProps = (state: App.State) => {
  return { cruisePromoBanners: state.cruise.cruisePromoBanners }
}

export default connect(mapStateToProps)(CruisePromoTiles)
